import React from 'react';
import Modal from '../Modal/Modal';
import Button, { ButtonType } from '../General/Button/Button';
import { PostingValidationResult } from '../../generated/graphql';
import moment from 'moment';

interface PostingConflictDialogProps {
    isOpen: boolean;
    validation: PostingValidationResult;
    onResolve: (resolution: 'replace' | 'edit' | 'cancel') => void;
}

const PostingConflictDialog: React.FC<PostingConflictDialogProps> = ({
    isOpen,
    validation,
    onResolve
}) => {
    if (!validation || validation.isValid) {
        return null;
    }

    const conflictingPosting = validation.conflictingPostings?.[0];

    const getDialogContent = () => {
        switch (validation.validationType) {
            case 'EXACT_DUPLICATE':
                return (
                    <div>
                        <h6 style={{ color: '#f0ad4e' }}>Duplicate Posting Detected</h6>
                        <p>A posting already exists with the same details:</p>
                        {conflictingPosting && (
                            <div style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px', marginBottom: '12px' }}>
                                <strong>{conflictingPosting.supplier?.name}</strong> → <strong>{conflictingPosting.terminal?.name}</strong>
                                <br />
                                <span style={{ color: '#666' }}>
                                    Cost: ${conflictingPosting.cost} | Effective: {moment(conflictingPosting.effectiveDate).format('MM/DD/YYYY')}
                                </span>
                            </div>
                        )}
                        <p>What would you like to do?</p>
                    </div>
                );
            case 'DATE_RANGE_OVERLAP':
                return (
                    <div>
                        <h6 style={{ color: '#f0ad4e' }}>Date Range Overlap</h6>
                        <p>This posting overlaps with an existing posting:</p>
                        {conflictingPosting && (
                            <div style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px', marginBottom: '12px' }}>
                                <strong>{conflictingPosting.supplier?.name}</strong> → <strong>{conflictingPosting.terminal?.name}</strong>
                                <br />
                                <span style={{ color: '#666' }}>
                                    Cost: ${conflictingPosting.cost} | Effective: {moment(conflictingPosting.effectiveDate).format('MM/DD/YYYY')}
                                </span>
                            </div>
                        )}
                        <p>Please adjust the dates or edit the existing posting to resolve the conflict.</p>
                    </div>
                );
            default:
                return (
                    <div>
                        <h6 style={{ color: '#d9534f' }}>Posting Conflict</h6>
                        <p>{validation.errorMessage}</p>
                    </div>
                );
        }
    };

    const getActionButtons = () => {
        const buttons = [];

        if (validation.validationType === 'EXACT_DUPLICATE') {
            // For exact duplicates, offer smart resolution options
            buttons.push(
                <Button
                    key="replace"
                    buttonType={ButtonType.Danger}
                    clickAction={() => onResolve('replace')}
                >
                    Replace Existing
                </Button>
            );

            buttons.push(
                <Button
                    key="edit"
                    buttonType={ButtonType.Primary}
                    clickAction={() => onResolve('edit')}
                >
                    Edit Existing Instead
                </Button>
            );
        }

        buttons.push(
            <Button
                key="cancel"
                buttonType={ButtonType.Secondary}
                clickAction={() => onResolve('cancel')}
            >
                Cancel
            </Button>
        );

        return buttons;
    };

    if (!isOpen) {
        return null;
    }

    const footerRightContent = (
        <div style={{ display: 'flex', gap: '8px' }}>
            {getActionButtons()}
        </div>
    );

    return (
        <Modal
            headerText="Posting Conflict Resolution"
            footerRightContent={footerRightContent}
        >
            {getDialogContent()}
        </Modal>
    );
};

export default PostingConflictDialog;